/**
 * Matches page.
 *
 * @module app/pages/MatchesPage
 */
import _ from 'lodash';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import EventViewAdapter from '../views/EventViewAdapter';
import MarketMenuAdapter from '../components/MarketMenuAdapter';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import SwiperNavigation from '../components/SwiperNavigation';
import URLProvider from '../components/URLProvider';
import LivescoreDiffusionAdapter from '../components/push/LivescoreDiffusionAdapter';
import MessageBusTopics from '../../common/MessageBusTopics';

/**
 * Internal state flag used to
 * determine whether page has been
 * rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 */
var args = null;

/**
 * Pagination
 *
 * @type {Object}
 */
var pagination = {

    /**
     * Base link
     *
     * @type {String}
     */
    baseLink: null,

    /**
     * Collection of pointers with first/last sub/group IDs
     *
     * @type {Array}
     */
    pointers: [],

    /**
     * Pointers for first/last group/subgroup
     *
     * @type {Object}
     */
    currentPointers: null,

    /**
     * XMLHttpRequest
     *
     * @type {XMLHttpRequest}
     */
    request: null,

    /**
     * Holding the next page to be requested.
     *
     * @type {Object}
     */
    nextPageNumber: null,

    /**
     * Lazy Loading initialize
     *
     * @returns {void}
     *
     * @private
     */
    init: function init() {

        if (window.WH.sportsbook.totalPages > 1) {

            pagination.initializePagination();

            var config = pagination.getConfigByType(window.WH.sportsbook.sortOrder);

            // init lazy loading
            BasePage.LazyLoadingViewAdapter.init({
                totalPages: window.WH.sportsbook.totalPages,
                container: config.container,
                group: config.group,
                subGroup: config.subGroup,
                row: config.row,
                selection: config.selection,
                selectionOdds: config.selectionOdds,
                replaceGroupStr: config.replaceGroupStr,
                replaceSubGroupStr: config.replaceSubGroupStr,
                totalItemsPerPage: window.WH.sportsbook.eventsPerPage,
                pagination: pagination,
                highlightedSelections: SelectionViewAdapter.highlightedSelections,
                timeSelector: '.localisable',
                totalRowsCount: window.WH.sportsbook.totalRowsCount,
            });
        }
    },

    /**
     * Reset pagination
     *
     * @returns {void}
     *
     * @private
     */
    reset: function reset() {

        BasePage.LazyLoadingViewAdapter.destroy();
        pagination.init();
    },

    /**
     * Gets config details for date or competition grouping
     *
     * @param {String} type 'date' or 'competition'
     *
     * @returns {Object}
     *
     * @private
     */
    getConfigByType: function getConfigByType(type) {

        var config = {

            date: {
                container: '.event-container',
                group: '.market-group-wrapper',
                subGroup: '.mg-wrapper, .btmarket__wrapper',
                row: '.event',
                selection: '.market__button, .oddsbutton',
                selectionOdds: '.selectiondetails, .betbutton__odds',
                replaceGroupStr: '<!-- subHeaderPlaceholder -->',
                replaceSubGroupStr: '<!-- eventPlaceholder -->',
            },
            competition: {
                container: '.event-container',
                group: '.comp-container',
                subGroup: '.mg-wrapper, .btmarket__wrapper',
                row: '.event',
                selection: '.market__button, .oddsbutton',
                selectionOdds: '.selectiondetails, .betbutton__odds',
                replaceGroupStr: '<!-- subHeaderPlaceholder -->',
                replaceSubGroupStr: '<!-- eventPlaceholder -->',
            },
        };

        return config[type] || {};
    },

    /**
     * Abort Ajax request if needed
     *
     * @returns {void}
     *
     * @private
     */
    abort: function abort() {

        if (pagination.request && typeof pagination.request.abort === 'function') {

            pagination.request.abort();
        }
    },

    /**
     * Gets more data to Lazy Load
     *
     * @param {Number} rowNumber
     * @param {String} direction
     * @param {Function} callback A function that accepts an {Object} with 'totalRows' and 'compiled'
     *
     * @returns {void}
     *
     * @private
     */
    getMoreData: function getMoreData(rowNumber, direction, callback) {

        if (pagination.nextPageNumber <= parseInt(window.WH.sportsbook.totalPages, 10)) {

            pagination.getPage(callback);
        } else {

            callback({ totalRows: 0, compiled: [] }, direction, rowNumber);
        }
    },

    /**
     * Get data for selected page
     *
     * @param {Function} callback A function that accepts an {Object} with 'totalRows' and 'compiled'
     *
     * @returns {void}
     *
     * @private
     */
    getPage: function getPage(callback) {

        pagination.request = deps.pageCommon.getJSONResource(
            pagination.baseLink + pagination.nextPageNumber + '.json',
            {
                'afterSuccess': pagination.getPageSuccess.bind(this, callback),
                'afterError': callback.bind(this, { totalRows: 0, compiled: [] }),
            }
        );

        pagination.nextPageNumber++;
    },

    /**
     * Do model updates
     *
     * @param {Object} model An object containing events, markets and selections
     *
     * @returns {void}
     *
     * @private
     */
    doModelUpdates: function doModelUpdates(model) {

        BasePage.ModelComposer.extendModel(model);
        pagination.updateSubscription(model);
    },

    /**
     * Function called after obtaining next page data to parse it and save it
     *
     * @param {Function} callback A function to call with the result of saving: nr of rows and compiled data
     * @param {Object} data An object containing pointers, model and compiled markup
     *
     * @returns {void}
     *
     * @private
     */
    saveNextPage: function saveNextPage(callback, data) {

        pagination.removeExistingGroupTemplate(
            pagination.pointers[pagination.pointers.length - 1].last,
            data.data.pointers.first,
            data.data.compiled
        );

        pagination.handlePointers(data.data.pointers);
        pagination.doModelUpdates(data.data.model);

        if (typeof callback === 'function') {

            callback({
                totalRows: _.size(data.data.model.markets),
                compiled: data.data.compiled,
            });
        }
    },

    /**
     * Function to handle a success response from fetching a page, but that does not contain any data.
     * This can occur when the server has an unhandled timeout for example.
     *
     * @param callback A function to call with the result of saving: nr of rows and compiled data
     *
     * @returns {void}
     *
     * @private
     */
    handleEmptyPageData: function handleEmptyPageData(callback) {

        if (typeof callback === 'function') {

            callback({
                totalRows: 0,
                compiled: [],
            });
        }
    },

    /**
     * Function called after a new page was successfully fetched. It handles the scenario where we have a
     * success response but might be empty, due to timeouts on the server for example.
     *
     * @param {Function} callback A function to call with the result of saving: nr of rows and compiled data
     * @param {Object} data Fetched from the server representing the page (model, pointers, compiled ...)
     *
     * @returns {void}
     *
     * @private
     */
    getPageSuccess: function getPageSuccess(callback, data) {

        if (!_.isEmpty(data) && !_.isEmpty(data.data) &&
            !_.isEmpty(data.data.pointers)) {

            pagination.saveNextPage(callback, data);
        } else {

            pagination.handleEmptyPageData(callback);
        }
    },

    /**
     * Removes existing subgroup template if present on current page
     *
     * @param {Object} currentPointers The current pointers to last group and subgroup
     * @param {Object} newPointers The new pointers to first group and subgroup
     * @param {Object} data Compiled object with grouped rows that contain markup
     *
     * @returns {void}
     *
     * @private
     */
    removeExistingSubgroupTemplate: function (currentPointers, newPointers, data) {

        var group = _.find(data, { 'key': newPointers.group });

        if (currentPointers.subgroup === newPointers.subgroup &&
            _.find(group.rows, { 'key': newPointers.subgroup })) {

            delete _.find(group.rows, { 'key': newPointers.subgroup }).template;
        }
    },

    /**
     * Removes existing group template if present on current page
     *
     * @param {Object} currentPointers The current last group and subgroup
     * @param {Object} newPointers The new first group and subgroup
     * @param {Object} data Compiled object with grouped rows that contain markup
     *
     * @returns {void}
     *
     * @private
     */
    removeExistingGroupTemplate: function removeExistingGroupTemplate(currentPointers, newPointers, data) {

        if (currentPointers.group === newPointers.group &&
            _.find(data, { 'key': newPointers.group })) {

            delete _.find(data, { 'key': newPointers.group }).template;
            pagination.removeExistingSubgroupTemplate(currentPointers, newPointers, data);
        }
    },

    /**
     * Initializes pagination
     *
     * @returns {void}
     *
     * @private
     */
    initializePagination: function initializePagination() {

        pagination.request = null;
        pagination.pointers = [];
        pagination.baseLink = window.WH.sportsbook.baseLink;
        pagination.handlePointers(window.WH.sportsbook.currentPointers);
        pagination.nextPageNumber = 3;
    },

    /**
     * This functions handles removing events from model
     *
     * @param {Array} eventsToRemove The collection of events to remove
     *
     * @returns {void}
     *
     * @private
     */
    updateModel: function updateModel(eventsToRemove) {

        BasePage.ModelComposer.removeEventsFromModel(eventsToRemove);
    },

    /**
     * Subscribed to given events and their descendants
     *
     * @param {Object} model The model object containing events, markets and selections
     *
     * @returns {void}
     *
     * @private
     */
    updateSubscription: function updateSubscription(model) {

        _.each(
            model,
            subscribeSetToPush
        );

        _.each(
            model.events,
            subscribeToLivescore
        );
    },

    /**
     * Add pointers
     *
     * @param {Object} newPointers The new pointers to first group and subgroup
     *
     * @returns {void}
     *
     * @private
     */
    handlePointers: function handlePointers(newPointers) {

        if (pagination.pointers.length >= 5) {

            pagination.pointers = pagination.pointers.slice(1, 5);
        }

        pagination.pointers.push(newPointers);
    },
};

/**
 * Function used to bind event handles to
 * various buttons/links on the page
 *
 * @param   {Object} content    jQuery object containing
 *                              the page content
 * @param   {Object} deps       Page dependencies
 * @param   {Object} args       Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var bindNavigation = function (content, deps, args) {

    initInternals(content, deps, args);
    MarketMenuAdapter.init();

    content
        .find('.filterlist')
        .on('click', '.filterlist__options__button', sortByType);
};

/**
 * Init the model
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
var initModel = function (regenerateModel) {

    BasePage.setOddsFormat();
    if (regenerateModel && regenerateModel === true) {
        BasePage.ModelComposer.regenerateMergedModel();
    }
    if (deps && deps.pageCommon) {
        deps.pageCommon.PushAdapter.unsubscribeAll();
        _.each(
            deps.sportsDataRepository.getModel(),
            subscribeSetToPush
        );
    }
};

/**
 * Init page interface, including filters menu, sort buttons
 * and livescore updates
 *
 * @returns {void}
 *
 * @private
 */
var initInterface = function () {

    initDayFilters();

    SelectionViewAdapter.init(
        deps.sportsDataRepository.getModel().selections
    );

    // Init livescore subscriptions
    _.each(
        deps.sportsDataRepository.getModel().events,
        subscribeToLivescore
    );

    SwiperNavigation.initializeAllSwipers();
};

/**
 * Performs initializations
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
var performInit = function (regenerateModel) {

    initModel(regenerateModel);
    initInterface();
};

/**
 * Subscribe an in play event to livescore
 * updates
 *
 * @param   {Object} event   Event model
 * @param   {String} eventId Event ID
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToLivescore = function (event, eventId) {

    if (event.isInPlay) {
        LivescoreDiffusionAdapter.subscribe(
            BasePage.getSportEnSlug(event),
            eventId,
            EventViewAdapter.livescore(eventId, content).update
        );
    }
};

/**
 * Subscribe a set of entities (events, markets, etc)
 * to PUSH updates
 *
 * @param   {Object} set An object containing entities
 *                       such as markets, selections, events
 *
 * @returns {void}
 *
 * @private
 */
var subscribeSetToPush = function (set) {

    _.each(set, subscribeEntityToPush);
};

/**
 * Subscribe a single entity to PUSH updates
 *
 * @param   {Object} entity Market/event/selection model
 *
 * @returns {void}
 *
 * @private
 */
var subscribeEntityToPush = function (entity) {

    if (entity && entity.id && !entity.id.match(/OB_TY/g) &&
        deps.pageCommon.DomPushAdapter.subscribe(entity, push.callback)) {

        deps.pageCommon.PushAdapter.subscribe(entity.pdsTopic);
    }
};

/**
 * Init internal data for later usage
 *
 * @param   {Object} content jQuery container element
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var initInternals = function (pageContent, pageDeps, pageArgs) {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;

    if (!_.isString(args.sortType) &&
        'competition' !== BasePage.PreferencesProvider.getPreference('sort')) {
        args.reload = true;
    }

    saveDefaultArgs();
};

/**
 * Setup and save args in preferences
 *
 * @returns {void}
 *
 * @private
 */
var saveDefaultArgs = function () {

    args = setPreferencesDefaults(args);

    BasePage.PreferencesProvider.setPreference('sort', args.sortType);
};

/**
 * Set preferences arguments for those missing
 *
 * @param   {Object} args
 *
 * @returns {Object}
 *
 * @private
 */
var setPreferencesDefaults = function (args) {

    if (rendered) {
        args.filter = setFilter(args.filter);
    }
    args.sortType = (
        !_.isNull(args.sortType) &&
        !_.isUndefined(args.sortType) &&
        !_.isEmpty(args.sortType)
    ) ? args.sortType : BasePage.PreferencesProvider.getPreference('sort');

    return args;
};

/**
 * Set default filter
 *
 * @param {String} filter
 *
 * @returns {void}
 *
 * @private
 */
var setFilter = function (filter) {

    return (
        !_.isNull(filter) &&
        !_.isUndefined(filter) &&
        !_.isEmpty(filter)
    ) ? filter :
        BasePage.jQuery('.filter-list__link--active').text().toLowerCase().trim();
};

/**
 * Init filters menu for matches page
 * based on available filters object in WHSports
 *
 * @param   {Object} WHSports WHSports object
 *
 * @returns {void}
 *
 * @private
 */
var initDayFilters = function () {

    content.find('.filterSelection').off('click', '.filter-list__link')
        .on('click', '.filter-list__link', dayFilterAction);
};

/**
 * Action handling click on a date filter
 *
 * @returns {void}
 *
 * @private
 */
var dayFilterAction = function () {

    args.filter = BasePage.jQuery(this).attr('data-filter').trim().toLowerCase();
    reloadPage();

    return false;
};

/**
 * Click event handler, where {this}
 * is a jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var sortByType = function () {

    var button = BasePage.jQuery(this);
    args.sortType = button.data('sort-type');
    button
        .addClass('-active')
        .siblings('a')
        .removeClass('-active');

    content
        .find('.filterlist').removeClass('-opened')
        .find('.filterlist__options').removeClass('-expanded').hide();

    reloadPage();

    return false;
};

/**
 * After page reload, update market dropdown links to include proper sort order
 *
 * @returns {void}
 *
 * @private
 */
var updateMarketDropdownLinks = function updateMarketDropdownLinks() {
    var currURLParts = BasePage.jQuery(this).attr('href').split('/');
    var updatedURL = api.getContentURL(_.extend({}, args, {
        marketGroupName: currURLParts.pop(),
    }));
    BasePage.jQuery(this).attr('href', updatedURL);
};

/**
 * After page reload, restore functionality
 *
 * @returns {void}
 *
 * @private
 */
var resubscribe = function () {

    [
        '.navHighlights',
        '.navCompetitions',
        '.navMatches',
    ].forEach(show);

    BasePage.jQuery('.column__primary')
        .find('a.topbets-viewall')
        .attr('href', '/betting/en-gb/apps/top-bets');

    BasePage.jQuery('#market-group-dropdown .dropdown__link').each(updateMarketDropdownLinks);
};

/**
 * Function to reload page fragment
 *
 * @returns {void}
 *
 * @private
 */
var reloadPage = function () {

    saveDefaultArgs();

    var afterSuccess = function () {

        performInit(true);
        MarketMenuAdapter.init();
        resubscribe();
        pagination.reset();
        BasePage.jQuery('.filterSelection .filter-list__link--active')
            .removeClass('-disabled disabled filter-list__link--active active');
        BasePage.jQuery('#filterSelection' + args.filter.charAt(0).toUpperCase() +
            args.filter.slice(1)).addClass('-disabled disabled filter-list__link--active active');

        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED, { skipTopBetsReload: true });
    };

    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.matches.matches(args),
        'redirectURL': URLProvider.matches.full(args),
    }, '.matches-events-container', { 'afterSuccess': afterSuccess });
};

/**
 * Custom show method specific to the project
 * Removes a displayNone class from an element
 * based on a given selector
 *
 * @param   {String} selector jQuery selector
 *
 * @returns {void}
 *
 * @private
 */
var show = function (selector) {

    BasePage.jQuery(selector).removeClass('displayNone');
};

/**
 * Hide the event container
 *
 * @param   {Boolean} show show flag
 *
 * @returns {void}
 *
 * @private
 */
var hideEventContainer = function (show) {

    BasePage.jQuery('#eventEntity').toggleClass('displayNone', !show);
    BasePage.jQuery('div.event-page-error').toggleClass('displayNone', show);
};

/**
 * Hides day tab
 *
 * @param   {Boolean} show show flag
 *
 * @returns {void}
 *
 * @private
 */
var hideDayTab = function (show) {

    var $dayTab = BasePage.jQuery('div.filter-nav > div');
    //Hide the whole tab
    if ($dayTab.children('a') === 1) {
        $dayTab.toggleClass('displayNone', !show);
    } else {
        $dayTab.children('a.active').toggleClass('displayNone', !show);
    }
};

/**
 * This function checks if the entity is the last one displayed in order to toggle
 * the market header
 *
 * @param {Object}  $entity jQuery element
 * @param {Boolean} hide    hide flag
 *
 * @returns {void}
 *
 * @private
 */
var manageMarketGroupHeader = function ($entity, hide) {

    // if it's the last of the market group we hide it
    if ($entity.hasClass('event') && isVisible($entity)) {
        $entity.parents('.market-group, .btmarket__wrapper').toggleClass('displayNone', hide);
    }
};

/**
 * Returns true if no more events are visible for the market group. This supports both new and old market
 * template
 *
 * @param {Object}  $entity jQuery element
 *
 * @returns {boolean} true if
 */
var isVisible = function ($entity) {

    return $entity.closest('.btmarket__wrapper').find('.event:visible').length === 0 ||
        $entity.siblings(':visible').length === 0;
};

/**
 * /**
 * Checks if error message must be displayed because no events are displayed
 * Also checks if we need to remove the day filter if gets empty
 *
 * @param   {Boolean} show show flag
 *
 * @returns {void}
 *
 * @private
 */
var manageEventAndTabs = function (show) {

    if (BasePage.jQuery('.event:visible').length === 0) {
        hideEventContainer(show);
        hideDayTab(show);
    }
};

/**
 * Function used to show the alternate handicap
 *
 * @param {Object} $event jQuery element for the event
 *
 * @return {void}
 *
 * @private
 */
var showAlternateHandicap = function showAlternateHandicap($event) {

    var handicapElement = $event.find('.selectionhandicap');

    if (handicapElement.length > 0) {

        var alternateHandicap = $event.find('.alternate-market').attr('data-hcapvalue');
        handicapElement.text(alternateHandicap);
    }
};

/**
 * Function used to display the livescore for an event
 * based on event element & event id
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {String} eventId Event ID
 *
 * @return {void}
 *
 * @private
 */
var showLivescore = function ($event, eventId) {

    $event.find('time').addClass('displayNone');

    if ($event.hasClass('markettable__event')) {

        $event.find('.area-livescore .event__status').removeClass('displayNone');
    } else {

        $event.find('.wh-label:first').removeClass('displayNone');
        showAlternateHandicap($event);
    }

    LivescoreDiffusionAdapter.subscribe(
        BasePage.getSportEnSlug($event),
        eventId,
        EventViewAdapter.livescore(eventId, content).update
    );
};

/**
 * Function used to display the start time for an event
 * based on event element & event id
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {String} eventId Event ID
 *
 * @return {void}
 *
 * @private
 */
var showStartTime = function ($event, eventId) {

    $event.find('.area-livescore time').removeClass('displayNone');
    $event.find('.area-livescore .event__status').addClass('displayNone');
    LivescoreDiffusionAdapter.unsubscribe(eventId);
};

/**
 * Hide an event if it's just been pushed to
 * isInPlay = true
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {Object} message PUSH message
 *
 * @return {void}
 *
 * @private
 */
var hideInPlayEvent = function ($event, message) {

    if (message.value === true) {
        manageMarketGroupHeader($event, true);
        manageEventAndTabs(false);
        $event.remove();
    }
};

/**
 * Show the alternate market for an event:
 * If the event has been pushed to isInPlay=true,
 * then hide the pre match market and display the
 * in play market.
 * If the event has been pushed to isInPlay=false,
 * then hide the in play market and display the pre match
 * market.
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {Object} message PUSH message
 *
 * @return {void}
 *
 * @private
 */
var showAlternateMarket = function ($event, message) {

    if ($event.find('[data-displayed=true][data-betinrun=' + (message.value) + ']').length > 0) {
        $event.find('[data-betinrun=' + (message.value) + ']').removeClass('displayNone');
        $event.find('[data-betinrun=' + (!message.value) + ']').addClass('displayNone');
    } else {
        manageMarketGroupHeader($event, true);
        manageEventAndTabs(false);
        $event.remove();
    }
};

/**
 * Show livescore or start time, based on isInPlay status
 * received from a PUSH message
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {Object} message PUSH message
 *
 * @return {void}
 *
 * @private
 */
var handleLivescore = function ($event, message) {

    if (message.value === true) {
        showLivescore($event, message.id);
    } else {
        showStartTime($event, message.id);
    }
};

/**
 * In case we access a sport with no day filters with a filter different then
 * 'today' or 'future' we are still setting the filter as 'today' and change your url.
 *
 * @return {void}
 *
 * @private
 */
var processDayFiltering = function () {

    if (!window.WH.sportsbook.isMajorSportWithFilters &&
        !_.isEmpty(args.filter) &&
        !_.includes(['today', 'future'], args.filter)) {

        args.filter = 'today';

        // Change URL and history
        deps.pageCommon.navigate(api.getContentURL(args), false, true);
    }
};

/**
 * Push related functionality
 *
 * @type {Object}
 */
var push = {

    /**
     * DOM PUSH update callback function
     * This function is called every time a push
     * message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @private
     */
    callback: function (message) {

        if (push.handlers.hasOwnProperty(message.attribute)) {
            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     */
    handlers: {
        'displayed': 'displayUpdateCase',
        'betInRun': 'betInRunUpdateCase',
    },

    /**
     * Handles the DOMPushAdapter messages affecting display
     *
     * @param  {Object} message DOM PUSH message
     *
     * @returns {void}
     *
     * @private
     */
    displayUpdateCase: function (message) {

        var $entity = BasePage.jQuery('#' + message.id);
        var show = message.value;
        manageEventAndTabs(show);
        manageMarketGroupHeader($entity, !message.value);
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     *
     * @param  {Object} message DOM PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateCase: function (message) {

        var $event = BasePage.jQuery('#' + message.id);

        BasePage.propagateTopBetsBetInRun(message);

        if ($event.find('.alternate-market').length === 0) {
            hideInPlayEvent($event, message);
        } else {
            $event.attr('data-betinrun', message.value);
            showAlternateMarket($event, message);
            handleLivescore($event, message);
        }

    },
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
var api = {

    /**
     * Return the URL associated with this page
     * based on the args
     *
     * @param   {Object} args Page arguments
     *
     * @returns {String}      Compiled URL based on args
     *
     * @public
     */
    getContentURL: function (args) {

        /**
         * Set of helper functions
         *
         * @type {Object}
         */
        var helperFunctions = {
            /**
             * Process sort type
             *
             * @returns {void}
             *
             * @private
             */
            processSortType: function () {

                return ((args.sortType) ? ('/' + args.sortType) : '');
            },
            /**
             * Process filter
             *
             * @returns {void}
             *
             * @private
             */
            processFilters: function () {

                return ((args.filter) ? ('/' + args.filter) : '');
            },
            /**
             * Process market group name
             *
             * @returns {String}
             *
             * @private
             */
            processMarketGroup: function processMarketGroup() {

                return ((args.marketGroupName) ? ('/' + args.marketGroupName) : '');
            },
        };

        args = setPreferencesDefaults(args);

        return '/betting/' + args.locale + '/' + args.sport + '/matches' +
            helperFunctions.processSortType() +
            helperFunctions.processFilters() +
            helperFunctions.processMarketGroup();
    },

    /**
     * Function called after the DOM ready event
     * is fired
     *
     * @param   {Object} deps Object that contains page
     *                        dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        BodyStateAdapter.init(BasePage.jQuery, 'matches', args.sport);

        if (deps.ready) {
            deps.ready('matches');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @param   {Object} deps       Object that contains page
     *                              dependencies
     * @param   {Object} content    jQuery object that contains
     *                              this page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: function () {

        BasePage.switchTab('Matches');
        performInit(false);

        if (args.hasOwnProperty('reload') && args.reload === true) {
            reloadPage();
        } else {
            pagination.init();
            processDayFiltering();
        }
    },

    bindNavigation: bindNavigation,

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: function () {

        rendered = true;
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: function () {

        BasePage.LazyLoadingViewAdapter.destroy();

        content = null;
        deps = null;
        args = null;
        BasePage.setObjectValues(pagination, {
            baseLink: null,
            pointers: [],
            currentPointers: null,
            request: null,
            nextPageNumber: null,
        });
        rendered = false;
    },

    /**
     * Returns the value of the internal
     * rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {

        return rendered;
    },

    /**
     * Slug to identify the current page
     */
    id: 'matches',
};

export default api;
